import styled from '@emotion/styled';
import usePullToRefresh from '@hooks/ui/usePullToRefresh';
import { ReactNode } from 'react';

interface Props {
    onRefresh: () => Promise<unknown> | void;
    children: ReactNode;
}

const INDICATOR_HEIGHT = 56;

function PullToRefresh({ onRefresh, children }: Props) {
    const {
        containerRef, pullDistance, isRefreshing, threshold,
    } = usePullToRefresh(onRefresh);

    const progress = Math.min(pullDistance / threshold, 1);
    const indicatorHeight = isRefreshing ? INDICATOR_HEIGHT : pullDistance;
    const shouldAnimate = !isRefreshing && pullDistance === 0;

    return (
        <Wrapper ref={containerRef}>
            <Indicator
                style={{
                    height: indicatorHeight,
                    transition: shouldAnimate ? 'height 0.25s ease' : 'none',
                }}>
                {isRefreshing && <Spinner />}
                {!isRefreshing && pullDistance > 0 && (
                    <Arrow style={{ opacity: progress, transform: `rotate(${progress * 180}deg)` }}>
                        ↓
                    </Arrow>
                )}
            </Indicator>
            {children}
        </Wrapper>
    );
}

const Wrapper = styled.div`
    width: 100%;
`;

const Indicator = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    width: 100%;
`;

const Spinner = styled.span`
    display: inline-block;
    width: 28px;
    height: 28px;
    border: 3px solid var(--color-primary);
    border-bottom-color: transparent;
    border-radius: 50%;
    animation: ptr-spin 0.8s linear infinite;

    @keyframes ptr-spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
    }
`;

const Arrow = styled.span`
    font-size: 20px;
    color: var(--color-primary);
    line-height: 1;
    display: inline-block;
`;

export default PullToRefresh;
